import styled from 'styled-components';
import { Link } from 'react-router-dom';
import React from 'react';
import { abstractBackgroundSoft } from './BackgroundPatterns';
import { techColors, neonGlow, alpha, terminalBorder, textGlowStrong, terminalTextStyle, terminalHeaderStyle } from './TechRetroEffects';
import { statusMessageAppearance, getStatusMessageColor } from './StatusMessageStyles';
import { formatUserMessage } from '../common';

export const AuthContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
  ${abstractBackgroundSoft}
  color: ${techColors.textPrimary};
  text-align: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
  ${terminalTextStyle}


  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 10px;
  }
`;

export const SecurityIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 4px;
  ${terminalBorder(techColors.neonPurple)}
  background: ${techColors.darkBgTertiary};
  color: ${techColors.neonPurple};
  font-size: 3em;
  margin-bottom: 20px;
  ${neonGlow(techColors.neonPurple, 'normal')}

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 2.5em;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    font-size: 2em;
    margin-bottom: 12px;
  }
`;

export const Title = styled.h1`
  font-size: 3em;
  color: ${techColors.neonPurple};
  margin-bottom: 1em;
  letter-spacing: 0.02em;
  position: relative;
  display: inline-block;
  ${textGlowStrong(techColors.neonPurple)}
  ${terminalHeaderStyle}

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: ${techColors.neonPurple};
    box-shadow: 0 0 10px ${techColors.neonPurple};
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.2em;
    
    &::after {
      width: 50px;
      height: 2px;
      bottom: -6px;
    }
  }

  @media (max-width: 480px) {
    font-size: 1.8em;
    margin-bottom: 0.8em;
    letter-spacing: 0.01em;
    
    &::after {
      width: 40px;
      height: 2px;
      bottom: -4px;
    }
  }
`;

export const StyledText = styled.p`
  font-size: 1em;
  color: ${techColors.textSecondary};
  line-height: 1.3;
  margin: 5px 0;
  text-align: center;
  ${terminalTextStyle}

  @media (max-width: 480px) {
    font-size: 0.9em;
  }
`;

export const Button = styled.button`
  color: ${techColors.textSecondary};
  background-color: transparent;
  font-size: 1em;
  font-weight: 500;
  padding: 12px 24px;
  border: 1px solid ${alpha(techColors.textSecondary, '50')};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    color: ${techColors.neonCyan};
    border-color: ${alpha(techColors.neonCyan, '80')};
    background-color: ${techColors.darkBgTertiary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.9em;
  }
`;

export const PrimaryButton = styled.button`
  color: ${techColors.darkBg};
  background-color: ${techColors.neonCyan};
  font-size: 1em;
  font-weight: 600;
  padding: 12px 24px;
  border: 1px solid ${techColors.neonCyan};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    ${neonGlow(techColors.neonCyan, 'strong')}
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.9em;
  }
`;

export const ExitButton = styled(Link)`
  color: ${techColors.textSecondary};
  background-color: transparent;
  font-size: 1em;
  font-weight: 500;
  padding: 12px 24px;
  border: 1px solid ${alpha(techColors.textSecondary, '50')};
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    color: ${techColors.neonCyan};
    border-color: ${alpha(techColors.neonCyan, '80')};
    background-color: ${techColors.darkBgTertiary};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.9em;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;

  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ModalContainer = styled.div`
  background-color: ${techColors.darkBgSecondary};
  ${terminalBorder(techColors.neonPurple)}
  padding: 12px;
  border-radius: 4px;
  max-width: 380px;
  width: 90%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-shadow: 0 0 30px ${alpha(techColors.neonPurple, '40')}, 0 0 60px ${alpha(techColors.neonPurple, '20')};
  transition: all 0.3s ease;
  animation: slideUp 0.3s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${techColors.neonPurple};
    background-size: 200% 100%;
    animation: shimmer 3s linear infinite;
    box-shadow: 0 0 10px ${techColors.neonPurple};
  }

  & h2 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: ${techColors.neonPurple};
    ${textGlowStrong(techColors.neonPurple)}
    ${terminalHeaderStyle}
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @media (max-width: 768px) {
    padding: 12px;
    max-width: 360px;
    border-radius: 8px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    max-width: 90%;
    border-radius: 8px;
    gap: 8px;
  }
`;

export const QRCodeImage = styled.img`
  width: 160px;
  height: 160px;
  margin: 0;
  border-radius: 4px;
  ${terminalBorder(techColors.neonPurple)}
  ${neonGlow(techColors.neonPurple, 'subtle')}

  @media (max-width: 480px) {
    width: 140px;
    height: 140px;
  }
`;

export const Input = styled.input`
  padding: 6px 10px;
  margin-bottom: 10px;
  ${terminalBorder(techColors.neonCyan)}
  border-radius: 4px;
  width: 100%;
  max-width: 120px; /* Уменьшили в два раза для кода (6 цифр) */
  font-size: 12px;
  text-align: center;
  background-color: ${techColors.darkBg};
  color: ${techColors.textPrimary};
  transition: all 0.3s ease;
  ${terminalTextStyle}

  &:focus {
    outline: none;
    ${neonGlow(techColors.neonCyan, 'strong')}
    border-color: ${techColors.neonCyan};
    background-color: ${techColors.darkBg};
    transform: translateY(-1px);
  }

  &::placeholder {
    color: ${techColors.textSecondary};
    opacity: 0.6;
    text-align: center;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    max-width: 100%;
    font-size: 11px;
    padding: 6px 10px;
    margin-bottom: 8px;
  }
`;

export const StatusCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'success',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  margin: 20px 0;
  background-color: ${techColors.darkBgSecondary};
  ${({ success }) => terminalBorder(getStatusMessageColor(success))}
  ${({ success }) => neonGlow(getStatusMessageColor(success), success === false ? 'normal' : 'subtle')}
  border-radius: 4px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 400px;

  &:hover {
    transform: translateY(-2px);
    ${({ success }) => neonGlow(getStatusMessageColor(success), 'strong')}
  }

  @media (max-width: 480px) {
    padding: 12px 20px;
    gap: 10px;
  }
`;

export const StatusIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'success',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  ${({ success }) => terminalBorder(getStatusMessageColor(success))}
  background-color: ${techColors.darkBgTertiary};
  color: ${({ success }) => getStatusMessageColor(success)};
  font-size: 1.5em;
  flex-shrink: 0;
  ${({ success }) => neonGlow(getStatusMessageColor(success), success === false ? 'normal' : 'subtle')}

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
    font-size: 1.3em;
  }
`;

export const StatusText = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'success',
})`
  color: ${({ success }) => getStatusMessageColor(success)};
  font-size: 1.1em;
  font-weight: 500;
  ${terminalTextStyle}

  @media (max-width: 480px) {
    font-size: 1em;
  }
`;

const StatusMessageBox = styled.p.withConfig({
  shouldForwardProp: (prop) => prop !== 'success',
})`
  position: absolute;
  top: calc(100% - 2px);
  left: 50%;
  transform: translateX(-50%);
  ${statusMessageAppearance}
  font-size: 11px;
  border-radius: 4px;
  padding: 6px 10px;
  min-height: 1.5em;
  display: ${({ children }) => (children ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  text-align: center;
  width: calc(100% - 24px);
  max-width: 300px;
  z-index: 1;
  animation: ${({ children }) => (children ? 'slideDown 0.3s ease-out' : 'none')};
  white-space: normal;
  word-wrap: break-word;
  max-width: calc(100% - 24px);
  margin-bottom: 0;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

export const StatusMessage = ({ success, children, ...props }) => (
  <StatusMessageBox success={success} {...props}>
    {typeof children === 'string' || typeof children === 'number'
      ? formatUserMessage(children)
      : children}
  </StatusMessageBox>
);

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 33px;
  min-height: 40px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-top: 2px;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 5px;
  }
`;

export const CompactButton = styled(Button)`
  padding: 5px 12px;
  font-size: 12px;
  ${terminalBorder(techColors.neonBlue)}
  border-radius: 4px;

  @media (max-width: 480px) {
    width: 100%;
    max-width: 300px;
  }
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${alpha(techColors.neonPurple, '30')};
  border-top: 3px solid ${techColors.neonPurple};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 20px auto;
  display: inline-block;
  box-shadow: 0 0 15px ${alpha(techColors.neonPurple, '40')};
  ${neonGlow(techColors.neonPurple, 'normal')}

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 480px) {
    width: 35px;
    height: 35px;
  }
`;
